import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { ResponsiveSpacingType, ResponsiveWidthType } from '@fiverr-private/theme/src/components/Box/types';
import {
  carouselScrollable,
  carouselContainer,
  carouselStack,
  carouselItem,
  fadeOverlayLeft,
  fadeOverlayRight,
  CAROUSEL_METRIC_VARIABLES,
} from './styles.ve.css';

export type ExtendedResponsiveSpacing =
  | ResponsiveSpacingType
  | {
      default?: string;
      sm?: string;
      md?: string;
      lg?: string;
      xl?: string;
      xxl?: string;
      xxxl?: string;
    };

export type ExtendedResponsiveWidth =
  | ResponsiveWidthType
  | {
      default?: string;
      sm?: string;
      md?: string;
      lg?: string;
      xl?: string;
      xxl?: string;
      xxxl?: string;
    };

export interface DragScrollCarouselProps {
  children: React.ReactNode[];
  gap?: ExtendedResponsiveSpacing | undefined;
  itemWidth?: ExtendedResponsiveWidth;
  className?: string;
  showFadeOverlay?: boolean;
  paddingSpacers?: boolean;
  disableWheel?: boolean;
  disableScroll?: boolean;
}

export const DragScrollCarousel: React.FC<DragScrollCarouselProps> = ({
  children,
  gap = '4',
  itemWidth = 'auto',
  className,
  showFadeOverlay = false,
  paddingSpacers = false,
  disableWheel = false,
  disableScroll = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const cssVariables = useMemo(() => {
    const gapObj = typeof gap === 'object' ? (gap as any) : null;
    const itemWidthObj = typeof itemWidth === 'object' ? (itemWidth as any) : null;

    const vars: Record<string, string> = {};

    if (gapObj?.xxl) {
      vars[CAROUSEL_METRIC_VARIABLES.gap.xxl] = gapObj.xxl;
    }
    if (gapObj?.xxxl) {
      vars[CAROUSEL_METRIC_VARIABLES.gap.xxxl] = gapObj.xxxl;
    }

    if (itemWidthObj?.xxl) {
      vars[CAROUSEL_METRIC_VARIABLES.itemWidth.xxl] = itemWidthObj.xxl;
    }
    if (itemWidthObj?.xxxl) {
      vars[CAROUSEL_METRIC_VARIABLES.itemWidth.xxxl] = itemWidthObj.xxxl;
    }

    return Object.keys(vars).length > 0 ? vars : null;
  }, [gap, itemWidth]);

  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const isDown = useRef(false);
  const startX = useRef<number>(0);
  const scrollLeftStart = useRef<number>(0);

  const checkScrollPosition = useCallback(() => {
    if (!showFadeOverlay || !scrollContainerRef.current) {
      return;
    }

    const slider = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = slider;
    const maxScroll = scrollWidth - clientWidth;

    setShowLeftFade(scrollLeft > 5);
    setShowRightFade(scrollLeft < maxScroll - 5);
  }, [showFadeOverlay]);

  useEffect(() => {
    const slider = scrollContainerRef.current;
    if (!slider) {
      return;
    }

    const handleMouseDown = (e: MouseEvent) => {
      isDown.current = true;
      startX.current = e.pageX - slider.offsetLeft;
      scrollLeftStart.current = slider.scrollLeft;

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDown.current) {
        return;
      }
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = x - startX.current;
      const newScrollLeft = scrollLeftStart.current - walk;

      const maxScroll = slider.scrollWidth - slider.clientWidth;

      if (newScrollLeft < 0) {
        const overScroll = Math.abs(newScrollLeft);
        const resistance = Math.min(overScroll * 0.3, 100);
        slider.scrollLeft = 0;
        slider.style.transform = `translateX(${resistance}px)`;
      } else if (newScrollLeft > maxScroll) {
        const overScroll = newScrollLeft - maxScroll;
        const resistance = Math.min(overScroll * 0.3, 100);
        slider.scrollLeft = maxScroll;
        slider.style.transform = `translateX(-${resistance}px)`;
      } else {
        slider.scrollLeft = newScrollLeft;
        slider.style.transform = '';
      }

      checkScrollPosition();
    };

    const handleGlobalMouseUp = () => {
      isDown.current = false;

      const hasTransform = slider.style.transform !== '';

      if (hasTransform) {
        slider.style.transition = 'transform 0.3s ease-out';
        slider.style.transform = '';

        setTimeout(() => {
          slider.style.transition = '';
          checkScrollPosition();
        }, 300);
      }

      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const scrollAmount = e.deltaY > 0 ? 100 : -100;
      slider.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    const handleScroll = () => {
      checkScrollPosition();
    };

    slider.addEventListener('mousedown', handleMouseDown);
    if (!disableWheel) {
      slider.addEventListener('wheel', handleWheel);
    }
    if (!disableScroll && showFadeOverlay) {
      slider.addEventListener('scroll', handleScroll);
    }

    checkScrollPosition();

    return () => {
      slider.removeEventListener('mousedown', handleMouseDown);
      if (!disableWheel) {
        slider.removeEventListener('wheel', handleWheel);
      }
      if (!disableScroll && showFadeOverlay) {
        slider.removeEventListener('scroll', handleScroll);
      }
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [checkScrollPosition, showFadeOverlay, disableWheel, disableScroll]);

  const standardGap =
    typeof gap === 'object'
      ? {
          default: gap.default,
          sm: gap.sm,
          md: gap.md,
          lg: gap.lg,
          xl: gap.xl,
        }
      : gap;

  const standardItemWidth =
    typeof itemWidth === 'object'
      ? {
          default: itemWidth.default,
          sm: itemWidth.sm,
          md: itemWidth.md,
          lg: itemWidth.lg,
          xl: itemWidth.xl,
        }
      : itemWidth;

  return (
    <Container
      position="relative"
      width="100%"
      overflow="hidden"
      className={`${className || ''} ${carouselContainer}`}
      style={cssVariables || undefined}
    >
      <Stack
        ref={scrollContainerRef}
        className={`${carouselScrollable} ${carouselStack}`}
        direction="row"
        overflowX="auto"
        cursor="grab"
        userSelect="none"
        gap={standardGap as ResponsiveSpacingType}
        paddingLeft={paddingSpacers ? { default: '5', sm: '8' } : undefined}
      >
        {children.map((child, index) => (
          <Container
            key={index}
            className={carouselItem}
            flexShrink={0}
            width={standardItemWidth as ResponsiveWidthType}
          >
            {child}
          </Container>
        ))}
        {paddingSpacers && <Container flexShrink={0} width={{ default: '20px', sm: '32px' }} />}
      </Stack>

      {showFadeOverlay && showLeftFade && (
        <Container
          position="absolute"
          pointerEvents="none"
          zIndex="1"
          className={fadeOverlayLeft}
          top={0}
          bottom={0}
          left={0}
          width="50px"
        />
      )}
      {showFadeOverlay && showRightFade && (
        <Container
          position="absolute"
          pointerEvents="none"
          zIndex="1"
          className={fadeOverlayRight}
          top={0}
          bottom={0}
          right={0}
          width="50px"
        />
      )}
    </Container>
  );
};

export default DragScrollCarousel;
